import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import cl from 'classnames'

import ProfileNavButton from '@components/Profile/NavButton'
import Search from '@components/Navigation/Search'

import { Container, Nav, Navbar, Alert } from 'react-bootstrap';
import Item from './Item/Item'

import type { NavigationProps, TopLinkItemType, NavigationWrapperProps } from './types'

import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faBars } from '@fortawesome/free-solid-svg-icons'
import brandStyles from './../_brand.module.scss'

import MENU from '@data/components/navigation'
import ThemeContext from '@contexts/themeContext'
import LoginContext from '@contexts/loginContext'

import { deleteCookie, getCookieByName } from '@helper/cookies'
import { getCountry } from '@helper/getConfigValues'
import { getLoginStatus } from '@api/getIsLoggedIn'
import ClientOnly from './../../ClientOnly';

import './Navigation.scss';
import { NEW_USER_COOKIE } from '@helper/constants';

const BrandLogo = React.lazy(() => import('./BrandLogo'))

function NavigationLinks({ menu }: { menu: TopLinkItemType[] }) {
  return (<>{menu.map(({ title, url, submenu }, idx) => (
    submenu
      ? <Item key={idx} title={title} submenu={submenu} />
      : <div key={idx} className="dropdownContainer nav-item"><Nav.Link href={url} key={url}><span>{title}</span></Nav.Link></div>
  ))}</>)
}

function NavigationWrapper({ isSearchActive, menu, search, profile }: NavigationWrapperProps) {
  const isDark = useContext(ThemeContext) === 'dark'
  const menuIconColour = isDark ? brandStyles.space : brandStyles.shade;
  const [newUserEmail, setNewUserEmail] = useState<string | null>(null);

  useEffect(() => setNewUserEmail(getCookieByName(NEW_USER_COOKIE)), []);

  const closeNewUserAlert = useCallback(() => {
    deleteCookie(NEW_USER_COOKIE);
    setNewUserEmail(null);
  }, []);

  return (<>
        {!!newUserEmail && <div className="wc-nav-floating-alert"><Alert variant={'primary'} dismissible={true} closeLabel={"X"} onClose={closeNewUserAlert}>
          To complete registration and claim WedPoints please follow the instructions we've sent to your email address: <strong>{newUserEmail}</strong>
        </Alert></div>}
      <Container fluid="xxl">
        <Navbar variant={isDark ? 'dark' : 'light'} expand="xxl" data-testid="navigation">
          <div className="wc-nav-brand-container">
            <Navbar.Toggle aria-controls="basic-navbar-nav" className="wc-navbar-toggle">
              <Icon icon={faBars} width={24} style={{color: menuIconColour}}/>
            </Navbar.Toggle>
            <Navbar.Brand href="/">
              <ClientOnly Component={BrandLogo} />
            </Navbar.Brand>
          </div>
          <Navbar.Collapse id="basic-navbar-nav" className={cl('wc-nav-links-container', { ["wc-nav-collapsed"]: isSearchActive })}>
            <Nav className="wc-nav-links-wrapper">
              <Navbar.Toggle aria-controls="basic-navbar-nav" className="wc-mobile-close-trigger">
                <div className="wc-nav-mobile-close-icon" >
                  &#10005;
                </div>
              </Navbar.Toggle>
              <span className="wc-nav-menu-items ew-menu-toggle d-sm-flex gap-lg-2 order-2 order-xl-1 justify-content-center">{menu}</span>
              <div className="wc-link-business nav-item order-3 order-xl-2">
                <a className="nav-link" href="/business/"><span>Advertise</span></a>
              </div>
              <div className="wc-search-container order-1 order-xl-3">
                {search}
              </div>
            </Nav>
            <Navbar.Toggle aria-controls="basic-navbar-nav" className="wc-mobile-nav-overlay wc-navbar-toggle wc-nav-collapsed"></Navbar.Toggle>
          </Navbar.Collapse>
          <div>
            {profile}
          </div>
        </Navbar>
      </Container>
      </>
  )
}

function Navigation({ menu = [], dark = false, iconUrl = '', messagingEnabled = false, editUserAvatar = undefined }: NavigationProps) {
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false)
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isCompetitionEnabled, setIsCompetitionEnabled] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isMessagingEnabled, setIsMessagingEnabled] = useState<boolean>(messagingEnabled);
  
  const PromoModal = React.lazy(() => import('./PromoModal'));

  useEffect(() => {
    getLoginStatus()
      .then(result => {
        if (!!result) {
          setIsLoggedIn(result.IsLoggedIn);
          setIsCompetitionEnabled(result.IsCompetitionEnabled);
          setShowModal(result.ShowPromoModal);

          if (result.MessagingEnabled !== undefined && isMessagingEnabled !== result.MessagingEnabled) {
            setIsMessagingEnabled(result.MessagingEnabled);
          }
        }
      });
  }, []);

  const countryCode = getCountry();

  const handleSearchFocus = () => {
    setIsSearchActive(true)
  }

  const handleCloseSearch = () => {
    setIsSearchActive(false)
  }

  const menuItems = useMemo(() => (
    [...MENU[countryCode], ...menu]
  ), [menu])

  return <ThemeContext.Provider value={dark ? 'light' : 'dark'}>
    <LoginContext.Provider value={{ isLoggedIn, setIsLoggedIn }}>
      <NavigationWrapper
        isSearchActive={isSearchActive}
        menu={<NavigationLinks menu={menuItems} />}
        search={
          <Search
            isActive={isSearchActive}
            handleFocus={handleSearchFocus}
            handleUnfocus={handleCloseSearch}
          />
        }
        profile={<>
          <ProfileNavButton
            isCompetitionEnabled={isCompetitionEnabled}
            iconUrl={iconUrl}
            messagingEnabled={isMessagingEnabled}
            editUserAvatar={editUserAvatar}
          />
        </>}
      />
      {showModal && <ClientOnly Component={PromoModal} />}
    </LoginContext.Provider>
  </ThemeContext.Provider>
}

export default Navigation